import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookie auth: every request carries the httpOnly access_token / refresh_token
// cookies set by the backend on login. No localStorage tokens, no Authorization
// header. Phase 2B switch (Feb 2026).
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Make sure the user object always carries an up-to-date business.niche.
  // If niche is missing or "other" (which can happen briefly after signup
  // before onboarding finishes provisioning), refetch /business/me and merge.
  const ensureBusinessNiche = async (userData) => {
    if (!userData || userData === false) return userData;
    const niche = userData?.business?.niche;
    if (!niche || niche === 'other') {
      try {
        const { data: biz } = await api.get('/business/me');
        if (biz) {
          return {
            ...userData,
            business: { ...(userData.business || {}), ...biz },
          };
        }
      } catch {}
    }
    return userData;
  };

  const checkAuth = async () => {
    // No access token in JS — just ask the server. The cookie rides along.
    try {
      const { data } = await api.get('/auth/me');
      const merged = await ensureBusinessNiche(data);
      setUser(merged);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    const userData = { ...data };
    delete userData.access_token;
    delete userData.refresh_token;
    const merged = await ensureBusinessNiche(userData);
    setUser(merged);
    return merged;
  };

  // Step 1 of 2-step login. Backend may either:
  //   (a) return access/refresh tokens (and Set-Cookie) when 2FA is OFF — log the user in, OR
  //   (b) return a {challenge, email_masked, ...} envelope when 2FA is ON.
  // The caller branches on `requires_otp` in the response.
  const loginInit = async ({ email, password, human_verified, honeypot, elapsed_ms }) => {
    const { data } = await api.post('/auth/login-init', {
      email, password, human_verified, honeypot, elapsed_ms,
    });
    if (data.access_token) {
      // 2FA OFF — finish login immediately. Cookie is already set by the server.
      const userData = { ...data };
      delete userData.access_token;
      delete userData.refresh_token;
      const merged = await ensureBusinessNiche(userData);
      setUser(merged);
    }
    return data;
  };

  // Step 2 of 2-step login. Sets cookie + user.
  const loginVerify = async ({ challenge, code }) => {
    const { data } = await api.post('/auth/login-verify', { challenge, code });
    const userData = { ...data };
    delete userData.access_token;
    delete userData.refresh_token;
    const merged = await ensureBusinessNiche(userData);
    setUser(merged);
    return merged;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    const userData = { ...data };
    delete userData.access_token;
    delete userData.refresh_token;
    const merged = await ensureBusinessNiche(userData);
    setUser(merged);
    return merged;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      const merged = await ensureBusinessNiche(data);
      setUser(merged);
      return merged;
    } catch { return null; }
  };

  // Refetch /business/me and merge into current user so things like the
  // INDUSTRY_CONFIG-driven sidebar/dashboard pick up niche immediately
  // after onboarding completes (no page reload required).
  const refreshBusiness = async () => {
    try {
      const { data: biz } = await api.get('/business/me');
      setUser(prev => (prev && prev !== false)
        ? { ...prev, business: { ...(prev.business || {}), ...biz } }
        : prev);
      return biz;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginInit, loginVerify, register, logout, refreshUser, refreshBusiness, api, formatError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { api, formatError };
